import React, { useState, useEffect } from "react";
import { Typography, CircularProgress, Button, OutlinedInput, InputLabel, MenuItem, FormControl, Select, Snackbar, IconButton, Alert } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import { DateRangePicker } from "@mui/x-date-pickers-pro/DateRangePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from 'dayjs';
import ReportCard from "./ReportCard";
import BillingTransactionReport from "./BillingReports/BillingTransactionReport";
import ClaimBalanceReport from "./BillingReports/ClaimBalanceReport";
import ClaimCollectionReport from "./BillingReports/ClaimCollectionReport";
import BillingOutstandingBalanceReport from "./BillingReports/BillingOutstandingBalanceReport";
import PA_UtilizationReport from "./BillingReports/PA_UtilizationReport";
import ServiceAuth from "./BillingReports/ServiceAuth";
import moment from "moment";
import axios from "axios";
import { APIURL } from "../../config";

export default function Billing(props) {
    const [isLoading, setIsLoading] = useState(true);
    const [reportList, setReportList] = useState([]);
    const [isReportLoading, setIsReportLoading] = useState(false);
    const [reportData, setReportData] = useState([]);
    const [customerList, setCustomerList] = useState([]);
    const [billingFilter, setBillingFilter] = useState({
        dateRange: [
            moment().startOf("month").format("YYYY-MM-DD"),
            moment().endOf("month").format("YYYY-MM-DD"),
        ],
    });
    const [selectedScreen, setSelectedScreen] = useState(null);
    const [custName, setCustName] = useState([]);
    const [cus_DDDIDs, setCus_DDDIDs] = useState([]);
    const [successText, setSuccessText] = useState("");
    const [errorText, setErrorText] = useState("");

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        axios
            .get(APIURL + "/jsreport/getAllReports", {
                params: { empID: props.user.emp_id },
            })
            .then((response) => {
                setReportList(response?.data?.reports.filter(function (ele) { return ele.report_group == "BILLING"; }) || []);
            });

        axios
            .get(APIURL + "/customers/v2/associated", {
                params: {
                    company_id: props.user.comp_id,
                    employee_id: props.user.emp_id,
                    type: props.user.permissions.AdminLevelCustomers ? "Admin" : "Manager"
                },
            })
            .then((response) => {
                setCustomerList(response?.data.filter((cus) => cus.cus_status === 'Active') || []);
            });

        setIsLoading(false);
    };

    const getReport = async (data, type) => {
        setSelectedScreen(null);
        setReportData([]);
        setIsReportLoading(true);

        const report_name = data?.report_details?.report_name || data?.report_name;
        switch (report_name) {
            case "Invoice Report":
                getInvoiceReport(type);
                break;
            case "Billing Transaction Report":
                getBillingTrans(type);
                break;
            case "Claim Balance Report":
                getClaimBalance(type);
                break;
            case "Billing Outstanding Balance Report":
                getOutstandingBalance(type);
                break;
            case "Claim Collection Report":
                getClaimCollect(type);
                break;
            case "Prior Auth Utilization Report":
                getPA_Utilization(type)
                break;
            default:
                break;
        }
    }

    const getInvoiceReport = async (type) => {
        axios
            .get(APIURL + "/jsreport/invoice", {
                params: {
                    com_id: props.user.comp_id,
                    emp_id: props.user.emp_id,
                    start: moment(billingFilter?.dateRange[0]).format("YYYY-MM-DD"),
                    end: moment(billingFilter?.dateRange[1]).format("YYYY-MM-DD"),
                },
                responseType: "arraybuffer",
                headers: {
                    Accept: "application/pdf",
                },
            })
            .then((response) => { setIsReportLoading(false); openPDF(response, type); });
    };

    const getBillingTrans = async (type) => {
        axios
            .get(APIURL + "/jsreport/billingTrans", {
                params: {
                    com_id: props.user.comp_id,
                    emp_id: props.user.emp_id,
                    month: moment().month() + 1,
                    cus_DDDIDs: cus_DDDIDs,
                    start: moment(billingFilter?.dateRange[0]).format("YYYY-MM-DD"),
                    end: moment(billingFilter?.dateRange[1]).format("YYYY-MM-DD"),
                },
                responseType: "arraybuffer",
                headers: {
                    Accept: "application/pdf",
                },
            })
            .then((response) => {
                setIsReportLoading(false);

                // Check if response is JSON and contains tooLarge flag
                const contentType = response.headers['content-type'];
                if (contentType && contentType.includes('application/json')) {
                    // Convert arraybuffer to string
                    const decoder = new TextDecoder('utf-8');
                    const jsonString = decoder.decode(response.data);
                    const json = JSON.parse(jsonString);
                    if (json.tooLarge) {
                        setReportData(json.claimData);
                        setSelectedScreen("Billing Transaction Report");
                        return;
                    }
                }

                openPDF(response, type);
            });
    };

    const getClaimBalance = async (type) => {
        axios
            .get(APIURL + "/jsreport/claimBalance", {
                params: {
                    com_id: props.user.comp_id,
                    emp_id: props.user.emp_id,
                    cutoff: moment().startOf("month").format("YYYY-MM-DD"),
                },
                responseType: "arraybuffer",
                headers: {
                    Accept: "application/pdf",
                },
            })
            .then((response) => {
                setIsReportLoading(false);

                // Check if response is JSON and contains tooLarge flag
                const contentType = response.headers['content-type'];
                if (contentType && contentType.includes('application/json')) {
                    // Convert arraybuffer to string
                    const decoder = new TextDecoder('utf-8');
                    const jsonString = decoder.decode(response.data);
                    const json = JSON.parse(jsonString);
                    if (json.tooLarge) {
                        setReportData(json.claimData);
                        setSelectedScreen("Claim Balance Report");
                        return;
                    }
                }

                openPDF(response, type);
            });
    };

    const getOutstandingBalance = async (type) => {
        axios
            .get(APIURL + "/jsreport/outstandingBalance", {
                params: {
                    com_id: props.user.comp_id,
                    emp_id: props.user.emp_id,
                    start: moment(billingFilter?.dateRange[0]).format("YYYY-MM-DD"),
                    end: moment(billingFilter?.dateRange[1]).format("YYYY-MM-DD"),
                },
                responseType: "arraybuffer",
                headers: {
                    Accept: "application/pdf",
                },
            })
            .then((response) => {
                setIsReportLoading(false);

                // Check if response is JSON and contains tooLarge flag
                const contentType = response.headers['content-type'];
                if (contentType && contentType.includes('application/json')) {
                    // Convert arraybuffer to string
                    const decoder = new TextDecoder('utf-8');
                    const jsonString = decoder.decode(response.data);
                    const json = JSON.parse(jsonString);
                    if (json.tooLarge) {
                        setReportData(json.claimData);
                        setSelectedScreen("Billing Outstanding Balance Report");
                        return;
                    }
                }

                openPDF(response, type);
            });
    };

    const getClaimCollect = async (type) => {
        axios
            .get(APIURL + "/jsreport/claimCollect", {
                params: {
                    com_id: props.user.comp_id,
                    emp_id: props.user.emp_id,
                    start: moment(billingFilter?.dateRange[0]).format("YYYY-MM-DD"),
                    end: moment(billingFilter?.dateRange[1]).format("YYYY-MM-DD"),
                },
                responseType: "arraybuffer",
                headers: {
                    Accept: "application/pdf",
                },
            })
            .then((response) => {
                setIsReportLoading(false);

                // Check if response is JSON and contains tooLarge flag
                const contentType = response.headers['content-type'];
                if (contentType && contentType.includes('application/json')) {
                    // Convert arraybuffer to string
                    const decoder = new TextDecoder('utf-8');
                    const jsonString = decoder.decode(response.data);
                    const json = JSON.parse(jsonString);
                    if (json.tooLarge) {
                        setReportData(json.claimData);
                        setSelectedScreen("Claim Collection Report");
                        return;
                    }
                }

                openPDF(response, type);
            });
    };

    const getPA_Utilization = async (type) => {
        axios
            .get(APIURL + "/jsreport/PA_Utilization", {
                params: {
                    com_id: props.user.comp_id,
                    emp_id: props.user.emp_id,
                    start: moment(billingFilter?.dateRange[0]).format("YYYY-MM-DD"),
                    end: moment(billingFilter?.dateRange[1]).format("YYYY-MM-DD"),
                },
                responseType: "arraybuffer",
                headers: {
                    Accept: "application/pdf",
                },
            })
            .then((response) => {
                setIsReportLoading(false);

                // Check if response is JSON and contains tooLarge flag
                const contentType = response.headers['content-type'];
                if (contentType && contentType.includes('application/json')) {
                    // Convert arraybuffer to string
                    const decoder = new TextDecoder('utf-8');
                    const jsonString = decoder.decode(response.data);
                    const json = JSON.parse(jsonString);
                    if (json.tooLarge) {
                        setReportData(json.claimData);
                        setSelectedScreen("Prior Authorization Utilization Report");
                        return;
                    }
                }

                openPDF(response, type);
            });
    };

    const openPDF = (response, reportAction = "open") => {
        const file = new Blob([response.data], { type: "application/pdf" });
        const fileURL = URL.createObjectURL(file);

        if (reportAction === "download") {
            const link = document.createElement("a");
            link.href = fileURL;
            link.download = "Report-" + new Date() + ".pdf";
            link.click();
        } else {
            window.open(fileURL, "_blank");
        }
    };

    const markFavourite = async (data, type) => {
        const payload = {
            report_id: data?.report_id,
            status: type,
            empID: props.user.emp_id
        };

        axios
            .put(APIURL + "/jsreport/markFavorite", payload)
            .then((response) => {
                if (response?.status === 200 && response?.data?.code === 200) {
                    setSuccessText(response?.data?.status_message || "Favorite Modifed Succesfully");
                } else {
                    setErrorText("Something went wrong");
                }

                fetchData();
            });
    }

    const markAllAsFavorite = () => {
        const notFavorite = reportList.filter((report) => report.status == 0);
        notFavorite.forEach((report) => markFavourite(report, 1));
    }

    const handleChange = (event) => {
        const {
            target: { value },
        } = event;
        setCustName(
            typeof value === 'string' ? value.split(',') : value,
        );
    };

    const handleMenuItemClick = (event) => {
        if (cus_DDDIDs.indexOf(event.target.id) === -1) {
            // event.target.id not found in DDDIDs, add it
            setCus_DDDIDs([...cus_DDDIDs, event.target.id]);
        } else {
            // event.target.id found in DDDIDs, remove it
            setCus_DDDIDs(cus_DDDIDs.filter((id) => id !== event.target.id));
        }
    }

    if (isLoading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', paddingLeft: 10, width: '100%' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">Loading...</span>
                </div>
            </div>
        )
    } else {
        return (
            <div style={{ paddingLeft: 10, width: '100%' }}>
                <div>
                    <div>
                        <Typography
                            style={{
                                fontWeight: "bold",
                                fontSize: "1.5rem",
                                fontFamily: "Lato",
                                marginBottom: 30,
                            }}
                        >
                            Billing Reports
                        </Typography>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex' }}>
                            <div>
                                <LocalizationProvider
                                    dateAdapter={AdapterDayjs}
                                    localeText={{ start: "From Date", end: "To Date" }}
                                >
                                    <DateRangePicker
                                        value={[dayjs(billingFilter?.dateRange[0]), dayjs(billingFilter?.dateRange[1])]}
                                        onChange={(newValue) => {
                                            setBillingFilter({
                                                ...billingFilter,
                                                dateRange: [new Date(newValue[0]), new Date(newValue[1])],
                                            });
                                        }}
                                        slotProps={{ fieldSeparator: { children: 'to' }, textField: { size: 'small' } }}
                                    />
                                </LocalizationProvider>
                            </div>
                            <div>
                                <FormControl sx={{ marginLeft: 2, width: 300 }} size="small">
                                    <InputLabel id="demo-multiple-name-label">Select Client</InputLabel>
                                    <Select
                                        labelId="select-client-label"
                                        id="select-client"
                                        multiple
                                        value={custName}
                                        onChange={handleChange}
                                        input={<OutlinedInput label="Select Client" />}
                                    >
                                        {customerList.map((cus) => (
                                            <MenuItem
                                                id={cus.DDDID}
                                                key={cus.cus_id}
                                                value={cus.cus_name}
                                                onClick={handleMenuItemClick}
                                            >
                                                {cus.cus_name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </div>
                            {isReportLoading &&
                                <div style={{ height: '1vh', marginLeft: 10 }}>
                                    <CircularProgress />
                                </div>
                            }
                        </div>
                        <div>
                            <Button variant="contained" onClick={markAllAsFavorite}>Mark all as "Favorite"</Button>
                        </div>
                    </div>
                </div>

                <div style={{ marginTop: 20, marginBottom: 10, width: '100%' }}>
                    <div>
                        {reportList.filter((r) => props.user.permissions[r.report_name.replaceAll(" ", "")] === true).map((e) => (
                            e.report_type === 1 ?
                                <ReportCard
                                    data={e}
                                    openReport={(type, data) => getReport(type, data)}
                                    markFavourite={(type, data) => markFavourite(type, data)}
                                />
                                :
                                <ReportCard
                                    data={e}
                                    openReport={(e) => { setSelectedScreen(e.report_name) }}
                                    markFavourite={(type, data) => markFavourite(type, data)}
                                />
                        ))}
                    </div>
                </div>
                <div style={{ bottom: "10px", width: '100%' }}>
                    <div>
                        {selectedScreen
                            ? <div>
                                {selectedScreen === "Billing Transaction Report" && <BillingTransactionReport user={props.user} data={reportData} />}
                                {selectedScreen === "Claim Balance Report" && <ClaimBalanceReport user={props.user} data={reportData} />}
                                {selectedScreen === "Claim Collection Report" && <ClaimCollectionReport user={props.user} data={reportData} />}
                                {selectedScreen === "Billing Outstanding Balance Report" && <BillingOutstandingBalanceReport user={props.user} data={reportData} />}
                                {selectedScreen === "Prior Authorization Utilization Report" && <PA_UtilizationReport user={props.user} data={reportData} />}
                                {selectedScreen === "Service Authorization Report" && <ServiceAuth {...props} billingFilter={billingFilter} screenName={'Service Authorization Report'} />}
                            </div>
                            : null
                        }
                    </div>
                </div>

                <Snackbar
                    open={successText !== ""}
                    autoHideDuration={30000}
                    onClose={() => setSuccessText("")}
                >
                    <Alert
                        sx={{ width: "100%" }}
                        severity={"success"}
                        elevation={6}
                        variant="filled"
                    >
                        {successText}
                        <IconButton
                            aria-label="close"
                            color="inherit"
                            sx={{ p: 0.25 }}
                            onClick={() => setSuccessText("")}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Alert>
                </Snackbar>
                <Snackbar
                    open={errorText !== ""}
                    autoHideDuration={30000}
                    onClose={() => setErrorText("")}
                >
                    <Alert
                        sx={{ width: "100%" }}
                        severity={"error"}
                        elevation={6}
                        variant="filled"
                    >
                        {errorText}
                        <IconButton
                            aria-label="close"
                            color="inherit"
                            sx={{ p: 0.25 }}
                            onClick={() => setErrorText("")}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Alert>
                </Snackbar>
            </div>
        )
    }
};